import { NSARouterMakerResult } from "namirasoft-account-react";
import { ValidatorCorrectorInferencerType, ValidatorInputRow, ValidatorOnInvalidAction, ValidatorRow, ValidatorType } from "namirasoft-inference";
import { NSBox, NSBoxBoolean, NSBoxEnum, NSBoxInteger, NSBoxString, NSBoxTextArea, NSColumn, NSRepeater, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import ConditionStringLine from "../components/ConditionStringLine";
import InferencerCommonFields from "../components/InferencerCommonFields";
import InferencerTypeID from "../components/InferencerTypeID";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";

interface ValidatorEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

interface ValidatorEditPageState
{
    on_invalid_action: ValidatorOnInvalidAction | null;
    type: ValidatorType | null;
}

const TEXT_FORMAT_TYPES: ValidatorType[] = [ValidatorType.Json, ValidatorType.Yaml, ValidatorType.Env, ValidatorType.Xml];

function isTextFormatType(type: ValidatorType | null): boolean
{
    return !!type && TEXT_FORMAT_TYPES.includes(type);
}

function isEnumType(type: ValidatorType | null): boolean
{
    return type === ValidatorType.Enum;
}

function hasFormatFields(type: ValidatorType | null): boolean
{
    return isTextFormatType(type) || isEnumType(type);
}

export default class ValidatorEditPage extends Component<ValidatorEditPageProps, ValidatorEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEnum_Type = createRef<NSBoxEnum<ValidatorType>>();
    NSBoxTextArea_ExpectedFormat = createRef<NSBoxTextArea>();
    NSBoxTextArea_ExampleFormat = createRef<NSBoxTextArea>();
    NSRepeater_ExpectedFormatEnum = createRef<NSRepeater<ConditionStringLine, string>>();
    NSBoxBoolean_InjectFormatGuidancePrompt = createRef<NSBoxBoolean>();
    NSBoxEnum_OnInvalidAction = createRef<NSBoxEnum<ValidatorOnInvalidAction>>();
    NSBoxString_ErrorMessage = createRef<NSBoxString>();
    InferencerTypeID_Corrector = createRef<InferencerTypeID<ValidatorCorrectorInferencerType>>();
    NSBoxInteger_RetryCount = createRef<NSBoxInteger>();
    NSBoxTextArea_AdditionalPrompt = createRef<NSBoxTextArea>();
    InferencerCommonFields_Ids = createRef<InferencerCommonFields>();
    NSBoxString_Description = createRef<NSBoxString>();

    private pendingExpectedFormatEnum: string[] | null = null;

    constructor(props: ValidatorEditPageProps)
    {
        super(props);
        this.state = { on_invalid_action: null, type: null };
        this.onInvalidActionChanged = this.onInvalidActionChanged.bind(this);
        this.onTypeChanged = this.onTypeChanged.bind(this);
    }

    private onInvalidActionChanged()
    {
        let raw = this.NSBoxEnum_OnInvalidAction.current?.getValueOne(false) as ValidatorOnInvalidAction | null | undefined;
        let next: ValidatorOnInvalidAction | null = raw ?? null;
        if (next !== this.state.on_invalid_action)
            this.setState({ on_invalid_action: next });
    }

    private onTypeChanged()
    {
        let raw = this.NSBoxEnum_Type.current?.getValueOne(false) as ValidatorType | null | undefined;
        let next: ValidatorType | null = raw ?? null;
        if (next !== this.state.type)
            this.setState({ type: next }, () => this.applyPendingFormatValues());
    }

    private applyPendingFormatValues()
    {
        if (this.pendingExpectedFormatEnum !== null && isEnumType(this.state.type))
        {
            this.NSRepeater_ExpectedFormatEnum.current?.setValue(this.pendingExpectedFormatEnum);
            this.pendingExpectedFormatEnum = null;
        }
    }

    private joinEnumValues(values: (string | null)[]): string
    {
        return values.filter((v): v is string => !!v && v.trim().length > 0).map(v => v.trim()).join(",");
    }

    private splitEnumValues(value: string | null): string[]
    {
        if (!value)
            return [];
        return value.split(",").map(v => v.trim()).filter(v => v.length > 0);
    }

    private renderEnumRepeater(
        repeaterRef: React.RefObject<NSRepeater<ConditionStringLine, string>>,
        title: string,
        keyPrefix: string,
        itemTitle: string
    )
    {
        return (
            <NSRepeater<ConditionStringLine, string>
                ref={repeaterRef}
                title={title}
                required={false}
                createItem={(gIndex: number, lIndex: number, ref: React.RefObject<ConditionStringLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                {
                    return <ConditionStringLine
                        ref={ref}
                        key={`${keyPrefix}_${gIndex}`}
                        index={lIndex}
                        title={itemTitle}
                        deletable={onDelete !== undefined}
                        onChanged={() => { onChange(); }}
                        onDelete={() => { onDelete?.(lIndex); }}
                    />;
                }}
                isEmpty={function (_, value: string | null): boolean
                {
                    return !ConditionStringLine.IS_DIRTY(value);
                }}
                getValue={function (item: ConditionStringLine, checkError: boolean): string | null
                {
                    return item.getValue(checkError);
                }}
                setValue={function (item: ConditionStringLine, _: number, value: string | null): void
                {
                    if (value)
                        item.setValue(value);
                }}
                style={{ width: "100%", maxWidth: NSBox.width.triple }}
            />
        );
    }

    private renderFormatTab(
        kind: "expected" | "example",
        textAreaRef: React.RefObject<NSBoxTextArea>,
        enumRepeaterRef: React.RefObject<NSRepeater<ConditionStringLine, string>>
    )
    {
        let type = this.state.type;
        let isExampleForEnum = kind === "example" && isEnumType(type);
        if (!hasFormatFields(type) || isExampleForEnum)
        {
            return (
                <NSColumn>
                    <div style={{ padding: 12, opacity: 0.6 }}>
                        {type
                            ? `${type} does not use ${kind === "expected" ? "an expected format" : "an example format"}.`
                            : "Select a Type first."}
                    </div>
                </NSColumn>
            );
        }
        if (isTextFormatType(type))
        {
            return (
                <NSColumn style={{ width: "100%" }}>
                    <NSBoxTextArea
                        ref={textAreaRef}
                        required={kind === "expected"}
                        title={kind === "expected" ? "Expected Format" : "Example Format"}
                        rows={12}
                        style={{ width: "100%", flex: 1 }}
                        info={{ link: `https://namirasoft.com/inference/console-guide/validator/#field-${kind}-format` }}
                    />
                </NSColumn>
            );
        }
        return (
            <NSColumn style={{ width: "100%" }}>
                {this.renderEnumRepeater(
                    enumRepeaterRef,
                    kind === "expected" ? "Expected Format Values" : "Example Format Values",
                    kind === "expected" ? "ValidatorExpectedEnum" : "ValidatorExampleEnum",
                    "Value"
                )}
            </NSColumn>
        );
    }

    override render()
    {
        let isCorrect = this.state.on_invalid_action === ValidatorOnInvalidAction.Correct;
        let isError = this.state.on_invalid_action === ValidatorOnInvalidAction.Error;

        return (
            <InferenceLayout<ValidatorRow, ValidatorInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.validator.edit : InferenceInfo.validator.create }}
                pages={{
                    entity: InferenceEntities.validator(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): ValidatorInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let type = this.NSBoxEnum_Type.current?.getValueEnumOne(ValidatorType, undefined as unknown as ValidatorType) as ValidatorType;

                                        let expected_format = "";
                                        let example_format: string | null = null;
                                        if (isTextFormatType(type))
                                        {
                                            expected_format = this.NSBoxTextArea_ExpectedFormat.current?.getValue() ?? "";
                                            example_format = this.NSBoxTextArea_ExampleFormat.current?.getValue() ?? null;
                                        }
                                        else if (isEnumType(type))
                                        {
                                            expected_format = this.joinEnumValues(this.NSRepeater_ExpectedFormatEnum.current?.getValue() ?? []);
                                            example_format = null;
                                        }

                                        let inject_format_guidance_prompt = this.NSBoxBoolean_InjectFormatGuidancePrompt.current?.getValue() ?? false;
                                        let on_invalid_action = this.NSBoxEnum_OnInvalidAction.current?.getValueEnumOne(ValidatorOnInvalidAction, undefined as unknown as ValidatorOnInvalidAction) as ValidatorOnInvalidAction;
                                        let error_message = on_invalid_action === ValidatorOnInvalidAction.Error
                                            ? (this.NSBoxString_ErrorMessage.current?.getValue() ?? null)
                                            : null;
                                        let corrector_inferencer_type: ValidatorCorrectorInferencerType | null = null;
                                        let corrector_inferencer_id: string | null = null;
                                        let retry_count: number | null = null;
                                        let additional_prompt: string | null = null;
                                        if (on_invalid_action === ValidatorOnInvalidAction.Correct)
                                        {
                                            let corrector = this.InferencerTypeID_Corrector.current?.getValue() ?? { inferencer_type: ValidatorCorrectorInferencerType.MODEL, inferencer_id: "" };
                                            corrector_inferencer_type = corrector.inferencer_type;
                                            corrector_inferencer_id = corrector.inferencer_id || null;
                                            retry_count = this.NSBoxInteger_RetryCount.current?.getValue() ?? null;
                                            additional_prompt = this.NSBoxTextArea_AdditionalPrompt.current?.getValue() ?? null;
                                        }
                                        let { memory_id, wiper_id, log_group_id } = this.InferencerCommonFields_Ids.current?.getValue() ?? InferencerCommonFields.EMPTY;
                                        let description = this.NSBoxString_Description.current?.getValue() ?? null;

                                        return {
                                            workspace_id: null,
                                            name,
                                            type,
                                            expected_format,
                                            example_format,
                                            inject_format_guidance_prompt,
                                            on_invalid_action,
                                            error_message,
                                            corrector_inferencer_type,
                                            corrector_inferencer_id,
                                            retry_count,
                                            additional_prompt,
                                            memory_id,
                                            wiper_id,
                                            log_group_id,
                                            description,
                                            validator_category: [],
                                            validator_field: [],
                                            validator_tag: [],
                                        };
                                    },
                                    setRow: (row: ValidatorRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEnum_Type.current?.setValue(row.type);

                                        if (isEnumType(row.type))
                                        {
                                            this.pendingExpectedFormatEnum = this.splitEnumValues(row.expected_format);
                                        }
                                        else
                                        {
                                            this.pendingExpectedFormatEnum = null;
                                        }
                                        this.setState({ type: row.type }, () =>
                                        {
                                            if (isTextFormatType(row.type))
                                            {
                                                this.NSBoxTextArea_ExpectedFormat.current?.setValue(row.expected_format);
                                                this.NSBoxTextArea_ExampleFormat.current?.setValue(row.example_format);
                                            }
                                            else if (isEnumType(row.type))
                                            {
                                                this.applyPendingFormatValues();
                                            }
                                        });

                                        this.NSBoxBoolean_InjectFormatGuidancePrompt.current?.setValueBool(row.inject_format_guidance_prompt);
                                        this.NSBoxEnum_OnInvalidAction.current?.setValue(row.on_invalid_action);
                                        this.setState({ on_invalid_action: row.on_invalid_action }, () =>
                                        {
                                            if (row.on_invalid_action === ValidatorOnInvalidAction.Error)
                                            {
                                                this.NSBoxString_ErrorMessage.current?.setValue(row.error_message);
                                            }
                                            else if (row.on_invalid_action === ValidatorOnInvalidAction.Correct)
                                            {
                                                this.InferencerTypeID_Corrector.current?.setValue({
                                                    inferencer_type: row.corrector_inferencer_type ?? ValidatorCorrectorInferencerType.MODEL,
                                                    inferencer_id: row.corrector_inferencer_id ?? "",
                                                });
                                                this.NSBoxInteger_RetryCount.current?.setValue(row.retry_count);
                                                this.NSBoxTextArea_AdditionalPrompt.current?.setValue(row.additional_prompt);
                                            }
                                        });
                                        this.InferencerCommonFields_Ids.current?.setValue({
                                            memory_id: row.memory_id,
                                            wiper_id: row.wiper_id,
                                            log_group_id: row.log_group_id,
                                        });
                                        this.NSBoxString_Description.current?.setValue(row.description);
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Expected Format",
                                            clicked: true,
                                            getContent: () => (
                                                <div key={`expected-format-${this.state.type ?? "none"}`} style={{ width: "100%", flex: 1 }}>
                                                    {this.renderFormatTab("expected", this.NSBoxTextArea_ExpectedFormat, this.NSRepeater_ExpectedFormatEnum)}
                                                </div>
                                            ),
                                        },
                                        {
                                            title: "Example Format",
                                            clicked: true,
                                            getContent: () => (
                                                <div key={`example-format-${this.state.type ?? "none"}`} style={{ width: "100%", flex: 1 }}>
                                                    {this.renderFormatTab("example", this.NSBoxTextArea_ExampleFormat, this.NSRepeater_ExpectedFormatEnum)}
                                                </div>
                                            ),
                                        },
                                        {
                                            title: "On Invalid",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxBoolean
                                                        ref={this.NSBoxBoolean_InjectFormatGuidancePrompt}
                                                        title="Inject Format Guidance Prompt"
                                                        required={false}
                                                        info={{ link: "https://namirasoft.com/inference/console-guide/validator/#field-inject-format-guidance-prompt" }}
                                                    />
                                                    <NSBoxEnum<ValidatorOnInvalidAction>
                                                        ref={this.NSBoxEnum_OnInvalidAction}
                                                        title="On Invalid Action"
                                                        required
                                                        multiple={false}
                                                        getEnumObject={() => ValidatorOnInvalidAction}
                                                        onChanged={this.onInvalidActionChanged}
                                                        style={{ width: "100%", maxWidth: NSBox.width.triple }}
                                                        info={{ link: "https://namirasoft.com/inference/console-guide/validator/#field-on-invalid-action" }}
                                                    />
                                                    {isError && (
                                                        <NSBoxString
                                                            ref={this.NSBoxString_ErrorMessage}
                                                            required={false}
                                                            title="Error Message"
                                                            style={{ width: "100%", maxWidth: NSBox.width.triple }}
                                                            info={{ link: "https://namirasoft.com/inference/console-guide/validator/#field-error-message" }}
                                                        />
                                                    )}
                                                    {isCorrect && (
                                                        <>
                                                            <NSColumn>
                                                                <InferencerTypeID<ValidatorCorrectorInferencerType>
                                                                    ref={this.InferencerTypeID_Corrector}
                                                                    {...this.props}
                                                                    typeTitle="Corrector Inferencer Type"
                                                                    idTitle="Corrector Inferencer ID"
                                                                    required
                                                                    getEnumObject={() => ValidatorCorrectorInferencerType}
                                                                    defaultType={ValidatorCorrectorInferencerType.MODEL}
                                                                    width={NSBox.width.triple}
                                                                    info={{ link: "https://namirasoft.com/inference/console-guide/validator/#field-corrector-inferencer" }}
                                                                />
                                                            </NSColumn>
                                                            <NSBoxInteger
                                                                ref={this.NSBoxInteger_RetryCount}
                                                                required={true}
                                                                defaultValue={0}
                                                                title="Retry Count"
                                                                style={{ width: "100%", maxWidth: NSBox.width.triple }}
                                                                info={{ link: "https://namirasoft.com/inference/console-guide/validator/#field-retry-count" }}
                                                            />
                                                            <NSBoxTextArea
                                                                ref={this.NSBoxTextArea_AdditionalPrompt}
                                                                required={false}
                                                                title="Additional Prompt"
                                                                style={{ width: "100%", maxWidth: NSBox.width.triple }}
                                                                info={{ link: "https://namirasoft.com/inference/console-guide/validator/#field-additional-prompt" }}
                                                            />
                                                        </>
                                                    )}
                                                </NSColumn>
                                            ),
                                        },
                                        {
                                            title: "Common",
                                            clicked: true,
                                            getContent: () => (
                                                <InferencerCommonFields
                                                    ref={this.InferencerCommonFields_Ids}
                                                    {...this.props}
                                                    hide={{ validator: true, budget: true }}
                                                    info_entity="validator"
                                                />
                                            ),
                                        },
                                    ]
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>

                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            style={{ width: NSBox.width.double }}
                            info={{ link: "https://namirasoft.com/inference/console-guide/validator/#field-name" }}
                        />
                        <NSBoxEnum<ValidatorType>
                            ref={this.NSBoxEnum_Type}
                            title="Type"
                            required
                            multiple={false}
                            getEnumObject={() => ValidatorType}
                            onChanged={this.onTypeChanged}
                            style={{ width: NSBox.width.one }}
                            info={{ link: "https://namirasoft.com/inference/console-guide/validator/#field-type" }}
                        />
                    </NSRow>
                    <NSBoxString
                        ref={this.NSBoxString_Description}
                        required={false}
                        title="Description"
                        style={{ width: "100%", maxWidth: NSBox.width.triple }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/validator/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}